import type { HandwritingIndex } from "../handwriting/handwriting-lookup.js";
import { readHandwritingIndex } from "../handwriting/handwriting-index.js";

/**
 * Getting the handwriting index to the page, once.
 *
 * The index is 568 KB of text, 272 KB over the wire, and it is fetched the
 * first time a reader opens the pad and never again. A page can carry three
 * search boxes (the navigation bar, the article and the rail) and every one of
 * them opens the same pad, so the fetch and the parse are shared here the way
 * `RouteSuggestions` shares the suggestion answers.
 *
 * **Nothing is fetched until a reader asks for it.** The index is worth more
 * than every other asset a page loads put together, and it is for the readers
 * who cannot type a character rather than for all of them. The pen button is
 * what pays for it.
 *
 * **A second open while the first fetch is in flight waits on that fetch.** The
 * promise is what is held rather than the index, so two boxes opened quickly
 * make one request.
 */

/**
 * How long a request for the index may hang before it is abandoned.
 *
 * Generous, because the file is 273 KB and a slow connection is not a broken
 * one. What this catches is the request that never answers at all. Without it
 * the promise below stays pending, every later open of the pad waits on that
 * same promise, and the reader is offered nothing for as long as the page is
 * open. Abandoned, the next open asks again.
 *
 * Exported because it is a fact about this class that a case has to wait out
 * rather than repeat.
 */
export const patience = 30_000;

/** Where the index is, and what has been made of it so far. */
export class HandwritingSource {
  readonly #url: string;
  readonly #fetch: typeof fetch;
  #loading: Promise<HandwritingIndex> | undefined;

  constructor(properties: {
    /** The published index's address, as the theme wrote it on the button. */
    readonly url: string;

    /** How to fetch it. The window's own, unless a test says otherwise. */
    readonly fetch: typeof fetch;
  }) {
    this.#url = properties.url;
    this.#fetch = properties.fetch;
  }

  /**
   * The index, fetching it if this is the first time it has been asked for.
   *
   * A fetch that fails resolves to an empty index rather than rejecting, and
   * the failure is not remembered. So a reader who opened the pad on a dropped
   * connection is told there are no candidates, and a second attempt asks
   * again.
   */
  async index(): Promise<HandwritingIndex> {
    this.#loading ??= this.#load();

    return this.#loading;
  }

  async #load(): Promise<HandwritingIndex> {
    try {
      return readHandwritingIndex(await this.#fetched());
    } catch {
      this.#loading = undefined;

      return [];
    }
  }

  /**
   * The published file.
   *
   * **Its own method, and awaited, which is what makes forgetting a failure
   * work.** The `catch` above clears the field the promise is about to be
   * assigned to, so it has to run after the assignment. An `await` on another
   * async call guarantees that. Calling `this.#fetch` from inside the `try`
   * did not: an async function runs to its first `await` where it stands, so a
   * fetch that threw outright cleared the field and had it filled in again
   * behind it. Measured, that failure was then remembered as an empty index and
   * never asked for a second time.
   *
   * A request that is refused throws rather than returning, since a refusal and
   * a failure are the same thing to a reader looking at an empty row.
   */
  async #fetched(): Promise<string> {
    const abandon = new AbortController();
    const abandoning = setTimeout(() => {
      abandon.abort();
    }, patience);

    try {
      const response = await this.#fetch(this.#url, {
        signal: abandon.signal,
      });

      if (!response.ok) {
        throw new Error(
          `The handwriting index answered ${String(response.status)}`,
        );
      }

      return await response.text();
    } finally {
      clearTimeout(abandoning);
    }
  }
}
